import { EmailTaskAttributes } from "@regal-voice/shared-types";
import { message } from "antd";
import partition from "lodash/partition";

import { getAttachment } from "Services/AgentApi/AgentApiService";
import { Attachment } from "Services/state/agent/desktopUI/AgentDesktopUISlice";

import { AttachmentInformation, EmailContent } from "../ActivityFeed/EmailThread/queries";

/**
 * Somewhat lax validation of what is considered an actual link. It does not do full validation, that should be its own effort.
 * Explanation
 *   [] outlines the first group (protocol + domain) to be a minimum of 2 characters and maximum 256
 *   (http(s)?):\/\/ optionally include http:// or https://
 *   (www\.)? optionally include www.
 *   a-zA-Z0-9@:%._\+~#= allows any alphanumeric character as well as @:%._+~#= symbols
 *   \. required dot
 *   [a-z]{2,6} lowercase letters minimum 2 characters maximum 6 (example "co", "com", "gov")
 *   \b is just a word separator
 *   []* group after protocol + domain, which can match 0 or more times (can be ports, paths, query strings)
 *   -a-zA-Z0-9@:%_\+.~#?&//= allows alphanumeric characters and the following symbols -@:%_+.~#?&/=
 * Source if you want to play around with it: https://regexr.com/39nr7
 * Note: We're trying ot make it easy for our users, the validation in the existing method isValidLink is too demanding, not allowing things like "google.com"
 * @param urlString
 * @returns if this string conforms a url
 */
export const isValidUrl = (urlString: string) => {
    return /^\s?[(http(s)?):\/\/(www\.)?a-zA-Z0-9@:%._\+~#=]{2,256}\.[a-z]{2,6}\b([-a-zA-Z0-9@:%_\+.~#?&//=]*)\s?$/.test(
        urlString
    );
};

export function generateForwardedMessageFromOriginalMessage({
    originalMessageEvent,
    originalMessageContent,
}: {
    originalMessageEvent: Pick<EmailTaskAttributes["originatingEmailEvent"], "senderEmail" | "subject" | "toEmails"> & {
        createdAt: string;
    };
    originalMessageContent: Pick<EmailContent, "htmlContent">;
}) {
    const date = originalMessageEvent.createdAt
        ? new Date(parseInt(originalMessageEvent.createdAt) * 1000).toLocaleString()
        : "";

    return `<br/><b>----Forwarded Message----</b><br/>From: ${
        originalMessageEvent.senderEmail.address
    }<br/>Date: ${date}<br/>Subject: ${originalMessageEvent.subject}<br/>To: ${originalMessageEvent.toEmails
        .map(({ address }) => address)
        .join(", ")}<br/><br/>${originalMessageContent.htmlContent}`;
}

export function getAndStoreAttachments({
    attachments,
    resetAttachments,
}: {
    attachments: Array<AttachmentInformation>;
    resetAttachments: (addedAttachments: Attachment[]) => void;
}) {
    return Promise.allSettled(
        attachments.map(async ({ name, url, mimeType }) =>
            // TODO: Check that this still works in the context of optimistic emails which
            // have a local URL and the file might??? still be in-memory?
            getAttachment(url).then(
                async (response): Promise<Attachment> => ({
                    type: mimeType,
                    name,
                    size: parseInt(
                        response.headers.get("content-length") || response.headers.get("Content-Length") || "0"
                    ),
                    localUrl: url,
                    media: Buffer.from(await response.arrayBuffer()),
                })
            )
        )
    ).then((attachmentFetchAttempts) => {
        const [successfulAttachments, failedAttachments] = partition(
            attachmentFetchAttempts,
            (result): result is PromiseFulfilledResult<Attachment> => result.status === "fulfilled"
        );

        if (failedAttachments.length > 0) {
            message.error(
                `Failed to load ${failedAttachments.length} attachment${
                    failedAttachments.length > 1
                        ? "s. If the issue persists, try downloading them from the original email and then manually uploading them."
                        : ""
                }.`,
                5000
            );
        }
        resetAttachments(successfulAttachments.map((result) => result.value));
    });
}
